import {
  AnalyticsAlertType,
  EmployeeRole,
  OrderGuideSortOrder,
  PackUnit,
  VariantPackCostType,
} from '@/types';
import {
  ConsumptionTaxName,
  ConsumptionTaxType,
  PaymentMethodType,
  SalesRepOverrideMode,
  SalesRepPortionType,
  VariantPackAvailabilityStatus,
  ViewableBy,
  ViewableByWithNone,
} from '@/enums';
import { TranslatedLanguage } from '@/utils/i18n';

// Can be either a string or a map of language code to translation string,
// such as {"en": "Hello", "es": "Hola"}
type Translation = string | Partial<Record<TranslatedLanguage, string>>;

export interface ExperimentMetadata {
  [key: string]: string | boolean | undefined;
  variant?: string;
}

export interface Experiment {
  key: string;
  metadata?: ExperimentMetadata;
}

export interface OrderSummaryInput {
  id: string;
  pattern?: string | null;
  pattern_invalid_message?: Translation | null;
  placeholder?: Translation | null;
  required: boolean;
  subtitle?: Translation | null;
  title: Translation;
  type: 'text' | 'textarea';
  viewable_by?: ViewableBy;
}

export enum OrderSummarySummationOptions {
  ITEM_TYPE = 'ITEM_TYPE',
  ITEM_WEIGHTS = 'ITEM_WEIGHTS',
  ORDER_ITEMS = 'ORDER_ITEMS',
  PALLET_COUNT = 'PALLET_COUNT',
  SUBTOTAL = 'SUBTOTAL',
  UNITS = 'UNITS',
}

export interface OrderSummarySectionHeaderConfig {
  header_separator?: string;
  variant_pack_group_ids?: string[];
}

export enum OrderGuideCarouselType {
  BRAND = 'BRAND',
  INTEREST = 'INTEREST',
  PROMOTION = 'PROMOTION',
  SAMPLE = 'SAMPLE',
}

export interface OrderGuideCarouselSettings {
  list_position: number;
  max_length?: number;
  title_override?: string;
}

export interface OrderGuideCarouselConfigType {
  config: OrderGuideCarouselSettings;
  type: OrderGuideCarouselType;
}

export interface OrderCopyingConfig {
  enabled_restaurant_roles: EmployeeRole[];
  enabled_supplier_roles: EmployeeRole[];
}

export type OrderGuideCarouselConfig = {
  [key in OrderGuideCarouselType]?: OrderGuideCarouselSettings;
};

export interface OrderSummaryConfig {
  additional_inputs?: OrderSummaryInput[];
  button_text_fields?: OrderSummarySummationOptions[];
  section_header_config?: OrderSummarySectionHeaderConfig;
  section_header_fields?: OrderSummarySummationOptions[];
}

export interface PaymentLinksConfig {
  enabled: boolean;
}

interface SalesRepConfigCalculationSettings {
  ignore_negatives_in_gross_profit_total: boolean | null;
}

export interface SalesRepDisplayConfig {
  cost_type?: VariantPackCostType | null;
  display_negative_value?: boolean;
  // optional display-only override for portion type when rendering/calculating values
  // currently used by 'gross_profit' display field only in restaurant-app
  portion_type?: SalesRepPortionType | null;
  title_override?: string | null;
  type: string;
}

interface SalesRepDisplayOptions {
  configs: SalesRepDisplayConfig[];
}

interface ObiSettings {
  items_of_interest_enabled: boolean;
}

export interface SalesPortalConfig {
  enabled: boolean;
}

export interface CommissionSettings {
  draft_order_commission_enabled: boolean;
  monthly_commission_enabled: boolean;
}

export interface SalesRepConfig {
  calculation_settings: SalesRepConfigCalculationSettings | null;
  can_set_manual_override_expiration?: boolean | null;
  commission_settings: CommissionSettings | null;
  customer_cost_type: VariantPackCostType | null;
  desired_portion_type: SalesRepPortionType | null;
  disable_gross_profit_display?: boolean | null;
  disable_override_editing: boolean | null;
  display_options: SalesRepDisplayOptions | null;
  obi_settings: ObiSettings | null;
  override_mode: SalesRepOverrideMode;
  peak_price_protection: boolean;
  price_floor_enabled: boolean | null;
  price_floor_mode: VariantPackCostType | null;
}

export interface TabCardConfig {
  cashback_percentage?: number | null;
  enabled?: boolean;
  image_url?: string | null;
  sign_up_url?: string | null;
}

interface SmartOrderConfig {
  email_threads_enabled?: boolean;
  inbound_history_enabled?: boolean;
  separate_employee_drafts_enabled?: boolean;
  surface_notes_enabled?: boolean;
}

interface TagPrintingConfig {
  batch_delivery_enabled: boolean;
  batch_tag_printing_enabled?: boolean;
  custom_tag_printing_enabled?: boolean;
  generic_tag_printing_enabled?: boolean;
  order_printing_enabled?: boolean;
  tag_printing_enabled: boolean;
}

export interface ConsumptionTax {
  name: ConsumptionTaxName;
  type: ConsumptionTaxType;
  value: number;
}

export interface AnalyticsAlertConfig {
  silence_notification_in_hours: number;
}

interface AnalyticsAlertsConfig {
  alerts: { [key in AnalyticsAlertType]: AnalyticsAlertConfig };
}

export interface CreditRequestConfig {
  credit_policy: string[];
  display_guarantee_information?: boolean;
  print_credit_request_enabled?: boolean;
  sort_reasons_alphabetically?: boolean;
  viewable_by: ViewableBy;
  wrap_reason_text_enabled?: boolean;
}

export interface ShortPayConfig {
  enabled?: boolean;
}

export interface JackHenryChecksConfig {
  check_capture_enabled?: boolean;
}

export interface KlarnaConfig {
  disabled?: boolean;
}

export interface ListItemDisplayConfig {
  enabled?: boolean;
}

export interface InsightsHubConfig {
  enabled: boolean;
  show_current_orders: boolean;
}

export enum ApplicationType {
  CATALOG_ONLY = 'CATALOG_ONLY',
  ORDER_AGENT_ONLY = 'ORDER_AGENT_ONLY',
  PAYMENTS_ONLY = 'PAYMENTS_ONLY',
}

export interface OfflineModeConfig {
  catalog_enabled?: boolean;
  foreground_sync_enabled?: boolean;
  order_queuing_disabled?: boolean;
}

export enum DefaultFilterMode {
  ALL = 'all',
  CURRENT = 'current',
  VALUES = 'values',
}

export enum LineItemNotesAccessMode {
  ALL = 'ALL',
  DSR_ONLY = 'DSR_ONLY',
}

export interface DefaultFilterConfig {
  field: string;
  mode?: DefaultFilterMode;
  values?: string[];
}

export interface SearchEntryConfig {
  default_filters: DefaultFilterConfig[];
}

export interface SearchConfig {
  order_guide: SearchEntryConfig;
  subcategories_filter_enabled?: boolean | null;
}

export enum OnDeviceScanningSearchType {
  EXACT_MATCH = 'EXACT_MATCH',
  PARTIAL_MATCH = 'PARTIAL_MATCH',
}

export interface OnDeviceScanningConfig {
  additional_product_info_enabled?: boolean | null;
  auto_select_lowest_uom?: boolean | null;
  default_matching_pack_unit?: PackUnit | null;
  local_scan_ux_enabled?: boolean | null;
  local_scan_ux_images_enabled?: boolean | null;
  prominent_scanning_entry_enabled?: boolean | null;
  search_type?: OnDeviceScanningSearchType;
}

export interface PriceTrendsConfig {
  customer_group_filter_enabled: boolean;
  gross_profit_percentage_toggle_enabled: boolean;
}

export interface PricingAgentConfig {
  enabled: boolean;
}

export enum CostChangeLookbackMode {
  COMPLETED_WEEK = 'COMPLETED_WEEK',
  LAST_CHANGE = 'LAST_CHANGE',
  ROLLING_WINDOW = 'ROLLING_WINDOW',
}

export interface PricingConfig {
  bookmarking_enabled?: boolean;
  cost_change_lookback_mode: CostChangeLookbackMode;
  cost_change_week_anchor_day?: number;
  customer_cost_type: VariantPackCostType;
  customer_table_enabled?: boolean;
  peer_range_column_enabled?: boolean;
  pricing_agent?: PricingAgentConfig;
  pricing_capture_enabled: boolean;
  pricing_management_enabled: boolean;
  publishing_enabled: boolean;
  publishing_enabled_employee_ids?: string[] | null;
  show_invoice_history_popover: boolean;
}

export interface OrderQuantityConfig {
  enforce_for_supplier_admins?: boolean;
  min_max_quantity_enabled?: boolean;
  min_max_quantity_v2_enabled?: boolean;
}

interface CheckInConfig {
  check_in_enabled?: boolean;
  fee_keywords?: string[];
}

export interface InvoicePDFVisibilityConfig {
  hidden_for?: Record<'operator' | 'distributor', EmployeeRole[]>;
}

export interface AvailabilityStatusConfig {
  enabled?: boolean;
  enabled_statuses_for_operator_employees?: VariantPackAvailabilityStatus[];
  enabled_statuses_for_supplier_employees?: Partial<
    Record<EmployeeRole, VariantPackAvailabilityStatus[]>
  >;
  sort_disabled_statuses_last?: boolean;
}

export interface LineItemNotesConfig {
  access_mode?: LineItemNotesAccessMode;
  enabled?: boolean;
  max_length?: number;
}

export interface PerformanceTracingConfig {
  emit_start_events?: boolean;
  enabled?: boolean;
  sample_rate?: number;
}

export interface SentryRestaurantAppTracingConfig {
  enabled?: boolean;
  sample_rate?: number;
}

export interface SentryTracingConfig {
  enabled?: boolean;
  restaurant_app?: SentryRestaurantAppTracingConfig;
}

export interface SentryConfig {
  tracing?: SentryTracingConfig;
}

export interface TypesenseConfig {
  catalog_category_lists?: boolean;
  filtering_enabled?: boolean;
}

export interface PromotionsConfig {
  discount_codes_enabled?: boolean;
}

export enum PackSortOrder {
  Asc = 'ASC',
  Desc = 'DESC',
}

export interface PackSortConfig {
  custom_order?: PackUnit[];
  sort_order?: PackSortOrder;
}

export interface DynamicConfig {
  account_selector_customer_group_filter_enabled?: boolean;
  additional_order_validation_supplier_roles?: EmployeeRole[];
  analytics_alerts_config?: AnalyticsAlertsConfig;
  application_type?: ApplicationType;
  are_substitutions_enabled?: boolean;
  assigned_sales_rep_visible?: boolean;
  autopay_enabled?: boolean;
  autopay_self_serve_enabled?: boolean;
  availability_status_config?: AvailabilityStatusConfig;
  brand_pages_enabled?: boolean;
  can_substitute_from_catalog?: boolean;
  card_view_v2_enabled?: boolean;
  cash_payments_enabled?: boolean;
  catalog_carousel_holdout?: boolean;
  catalog_filtering_enabled?: boolean;
  catalog_group_items_expanded_enabled?: boolean;
  catalog_lazy_carousels_enabled?: boolean;
  catalog_search_ux_enabled?: boolean;
  category_images_enabled?: boolean;
  check_in_config?: CheckInConfig;
  consumption_tax?: ConsumptionTax[];
  credit_request_config?: CreditRequestConfig;
  credit_request_enabled?: boolean;
  crm_activities_enabled?: boolean;
  disable_pepper_invoice_pdf?: boolean;
  enabled_customer_facing_payment_methods?: PaymentMethodType[];
  experiments?: Experiment[];
  hide_placeholder_images?: boolean;
  insights_hub_config?: InsightsHubConfig;
  intercom_enabled?: boolean;
  invoice_pdf_visibility_config?: InvoicePDFVisibilityConfig;
  is_analytics_dashboard_enabled?: boolean;
  is_chatbot_enabled?: boolean;
  item_groups_enabled?: boolean;
  item_lead_time_settings_enabled?: boolean;
  item_recommendations_enabled?: boolean;
  item_subscriptions_enabled?: boolean;
  jack_henry_checks_config?: JackHenryChecksConfig;
  klarna_config?: KlarnaConfig;
  landing_page_enabled?: boolean;
  large_screen_modal_holdout?: boolean;
  limited_app_enabled?: boolean;
  line_item_notes_config?: LineItemNotesConfig;
  list_item_display_config?: ListItemDisplayConfig;
  manual_check_payments_enabled?: boolean;
  menu_costing_enabled?: boolean;
  menu_image_og_generation_enabled?: boolean;
  menu_og_generation_enabled?: boolean;
  offline_mode_config?: OfflineModeConfig;
  offline_mode_enabled?: boolean;
  on_demand_payment_statement_enabled?: boolean;
  on_device_scanning_config?: OnDeviceScanningConfig;
  order_copying_config?: OrderCopyingConfig;
  order_guide_carousel_config?: OrderGuideCarouselConfig;
  order_guide_catalog_search_enabled?: boolean;
  order_guide_default_sort_order?: OrderGuideSortOrder;
  order_guide_unified_search_enabled?: boolean;
  order_quantity_config?: OrderQuantityConfig;
  order_summary_config?: OrderSummaryConfig;
  order_summary_sections_enabled?: boolean;
  pack_sort_config?: PackSortConfig;
  pay_upon_ordering?: boolean;
  payment_links_config?: PaymentLinksConfig;
  payments_ftni?: boolean;
  payments_only_mode_enabled?: boolean;
  performance_tracing_config?: PerformanceTracingConfig;
  price_quote_generation_enabled?: boolean;
  price_trends_config?: PriceTrendsConfig;
  pricing_config?: PricingConfig;
  print_order_enabled?: boolean;
  print_par_sheet_enabled?: boolean;
  promotions_config?: PromotionsConfig;
  prospect_finder_enabled?: boolean;
  prospect_item_prices_viewable_by?: ViewableByWithNone | null;
  require_payment_method_on_order_placement?: boolean;
  require_substitution_action?: boolean;
  reset_fulfillment_on_order_submission?: boolean;
  sales_hub_check_in_enabled?: boolean;
  sales_portal?: SalesPortalConfig;
  sales_rep_ai_orders_enabled?: boolean;
  sales_rep_config?: SalesRepConfig;
  salt_api_integration_enabled?: boolean;
  salt_cache_read_enabled?: boolean;
  search_config?: SearchConfig;
  sentry_config?: SentryConfig;
  short_pay_config?: ShortPayConfig;
  smart_order_config?: SmartOrderConfig;
  smart_order_enabled?: boolean;
  sort_categories_with_special_chars_last?: boolean;
  storefront_multi_cart_enabled?: boolean;
  tab_card_config?: TabCardConfig;
  tag_printing_config?: TagPrintingConfig;
  timeslot_cutoff_fulfillment_hydration_enabled?: boolean;
  transaction_fee_name?: string | null;
  typesense_config?: TypesenseConfig;
}
